import React, { useState, useEffect } from 'react';
import { CubeIcon, TicketIcon } from '@heroicons/react/24/outline';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput from '../../pro/components/orderform/SelectInput';
import Icon from '../../pro/components/orderform/Icon';
import Card from '../components/Card';
import AutoCalcRow from '../components/AutoCalcRow';
import type { WarehouseFields, HandlingItemRow, ProductRateRow } from '../fixtures';
import type { HandlingCatalogRow, ProductRateFieldsPayload, RateStandards, WhseCustomerRow } from '../../../services/quote.service';
import { ACCOUNT_TYPES, STORAGE_TYPES, DO_NOT_BILL_REASONS, STORAGE_UNIT_TYPES, HANDLING_UNIT_TYPES, FUEL_TYPES_WAREHOUSE, CHARGE_RATE_TYPES, withCurrent } from '../quoteOptions';
import { transloadGuard, belowMinimum } from '../rateGuards';

const LBL = 130;

interface Props {
  createMode?: boolean;
  data: WarehouseFields;
  /** Optional change callback (same contract as DirectTab.onDirectChange):
   *  when provided, inputs become controlled-editable and emit
   *  `(field, value)` with the camelCase WarehouseFields key. */
  onWarehouseChange?: (field: keyof WarehouseFields, value: string) => void;
  /** Standards/minimums payload (GET /quotes/rate-standards) for below-min hints. */
  rateStandards?: RateStandards | null;
  /** handlingquote rows + CRUD pass-throughs (Handling In / Out lists). */
  handlingItems?: HandlingItemRow[];
  handlingCatalog?: HandlingCatalogRow[];
  onAddHandling?: (p: { handlingitemrecno: number; handlingtype: 'handlingin' | 'handlingout'; handlingratetype: string; handlingrate: number; handlingunittype?: string }) => void;
  onChangeHandling?: (id: number, patch: { handlingratetype?: string; handlingrate?: number; handlingunittype?: string }) => void;
  onDeleteHandling?: (id: number) => void;
  /** Product-rate CRUD pass-throughs (per-SKU storage/handling overrides). */
  onAddProductRate?: (p: ProductRateFieldsPayload) => void;
  onUpdateProductRate?: (id: number, p: ProductRateFieldsPayload) => void;
  onDeleteProductRate?: (id: number) => void;
  onProductCodeLookup?: (customercode: number) => Promise<Array<{ recno: number; sku: string; description: string; masterUom: string }>>;
  /** N60: warehouse-customer name search returning product codes in one call. */
  onWhseCustomerLookup?: (q: string) => Promise<WhseCustomerRow[]>;
}

/**
 * Warehouse tab for WAREHOUSE quotes only. Storage + handling fields from
 * transloadquote. Product Rates list is still static — productcoderates
 * isn't wired yet.
 */
const WarehouseTab: React.FC<Props> = ({
  data, onWarehouseChange, rateStandards = null, createMode = false,
  handlingItems = [], handlingCatalog = [], onAddHandling, onChangeHandling, onDeleteHandling,
  onAddProductRate, onUpdateProductRate, onDeleteProductRate, onProductCodeLookup,
  onWhseCustomerLookup,
}) => {
  // Helper to keep current value in the options list even if it's something
  // the static list doesn't cover (deduplicates while preserving order).
  const dedupOpts = (current: string, rest: string[]) =>
    [current, ...rest].filter((v, i, a) => v && a.indexOf(v) === i);

  const ch = (field: keyof WarehouseFields) =>
    onWarehouseChange ? (v: string) => onWarehouseChange(field, v) : undefined;

  // N16: storage sub-fields are only valid for certain storagetypes
  // (formatview_quote.pl:1426-1466). Disable + blank a field that doesn't
  // apply to the current type so a mis-typed storage quote can't be entered
  // (the server guard in apply_save_rules is the integrity backstop).
  const STORAGE_ALLOWED: Record<string, string[]> = {
    storageAnniversaryDays: ['ANNIVERSARY'],
    storageFreeDays: ['ANNIVERSARY', 'MANUAL'],
    storageFreePallets: ['AVERAGE', 'SPLIT MONTH', 'MANUAL'],
    storageRcvMonthFree: ['ANNIVERSARY', 'SPLIT MONTH', 'MANUAL'],
    storageDoNotBillReason: ['DO NOT BILL'],
  };
  const storageAllows = (field: string) =>
    STORAGE_ALLOWED[field].includes((data.storageType || '').toUpperCase());
  // N21 (defs_quote.pl:308-321): a DO NOT BILL storage type requires a reason.
  const doNotBillReasonMissing =
    storageAllows('storageDoNotBillReason') && !data.storageDoNotBillReason;

  const totalDrayHandling = computeTotalDrayHandling(data);

  // #13: the main handling/transload charge is guarded by transloadstandards
  // (V1 getHandlingStandards loads transloadstandards into handlingstandards,
  // keyed by whsetype/packagetype/movetype × size). Warn below minimum.
  const handlingGuard = transloadGuard(rateStandards, data.whseType, data.packageType, data.moveType, data.size);
  const handlingBelowMin = belowMinimum(handlingGuard, data.drayHandlingCharge);

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <Card icon={<CubeIcon className="h-3.5 w-3.5" />} title="Account & Storage">
          <Row>
            <Label width={LBL}>Account Type</Label>
            <SelectInput
              ariaLabel="Account Type"
              value={data.accountType}
              onChange={ch('accountType')}
              highlight={createMode && !data.accountType ? 'red' : 'yellow'}
              options={dedupOpts(data.accountType, ACCOUNT_TYPES)}
            disabled={!onWarehouseChange} />
          </Row>
          <Row>
            <Label width={LBL}>Storage Type</Label>
            <SelectInput highlight={createMode && !data.storageType ? 'red' : undefined} ariaLabel="Storage Type" value={data.storageType} onChange={ch('storageType')} options={dedupOpts(data.storageType, STORAGE_TYPES)} disabled={!onWarehouseChange} />
            <Label># Anniv Days</Label>
            <TextInput value={storageAllows('storageAnniversaryDays') ? String(data.storageAnniversaryDays ?? 0) : ''} onChange={ch('storageAnniversaryDays')} ariaLabel="Anniversary Days" readOnly={!onWarehouseChange || !storageAllows('storageAnniversaryDays')} width={48} />
          </Row>
          {(!createMode || storageAllows('storageDoNotBillReason')) && (
          <Row>
            <Label width={LBL}>Do Not Bill Reason</Label>
            <SelectInput value={storageAllows('storageDoNotBillReason') ? data.storageDoNotBillReason : ''} onChange={ch('storageDoNotBillReason')} ariaLabel="Do Not Bill Reason" disabled={!onWarehouseChange || !storageAllows('storageDoNotBillReason')} highlight={doNotBillReasonMissing ? 'red' : undefined} placeholder="—" options={dedupOpts(data.storageDoNotBillReason, ['', ...DO_NOT_BILL_REASONS])} />
            {doNotBillReasonMissing && <span className="text-[10.5px] text-red-600 dark:text-red-400 font-semibold">required</span>}
          </Row>
          )}

          <Divider />

          {(!createMode || data.storageType !== 'DO NOT BILL') && <>
          <Row>
            <Label width={LBL}>Storage Rate</Label>
            <TextInput ariaLabel="Storage Rate" highlight={createMode && !Number(data.storageRate) ? 'red' : undefined} value={data.storageRate} onChange={ch('storageRate')} width={80} readOnly={!onWarehouseChange} />
            <Label>Per</Label>
            <SelectInput ariaLabel="Storage Unit Type" value={data.storageUnitType} onChange={ch('storageUnitType')} options={dedupOpts(data.storageUnitType, STORAGE_UNIT_TYPES)} width={80} disabled={!onWarehouseChange} />
          </Row>
          <Row>
            <Label width={LBL}>Long Term Storage Rate</Label>
            <TextInput value={data.storageRateLongTerm} onChange={ch('storageRateLongTerm')} width={80} readOnly={!onWarehouseChange} />
            <Label>Days</Label>
            {/* storagelongtermdays lives on masterquote, not transloadquote —
                shown read-only as 0 until the header field is threaded in. */}
            <TextInput value="0" width={56} />
          </Row>
          </>}
          <Row>
            {(!createMode || storageAllows('storageFreeDays')) && <>
            <Label width={LBL}>Free Days</Label> <TextInput value={storageAllows('storageFreeDays') ? String(data.storageFreeDays ?? (createMode ? '' : 0)) : ''} onChange={ch('storageFreeDays')} ariaLabel="Free Days" readOnly={!onWarehouseChange || !storageAllows('storageFreeDays')} width={56} /></>}
            {(!createMode || storageAllows('storageRcvMonthFree')) && <>
            <Label>Rcv Month</Label> <SelectInput value={storageAllows('storageRcvMonthFree') ? data.storageRcvMonthFree : ''} onChange={ch('storageRcvMonthFree')} ariaLabel={createMode ? "Receiving Month Free" : "Rcv Month Free"} disabled={!onWarehouseChange || !storageAllows('storageRcvMonthFree')} options={['N', 'Y']} width={56} /></>}
          </Row>
          <Row>
            <Label width={LBL}>Free Pallets</Label>
            <TextInput value={storageAllows('storageFreePallets') ? String(data.storageFreePallets ?? 0) : ''} onChange={ch('storageFreePallets')} ariaLabel="Free Pallets" readOnly={!onWarehouseChange || !storageAllows('storageFreePallets')} width={80} />
          </Row>
        </Card>

        <Card icon={<Icon.ship className="h-3.5 w-3.5" />} title="Handling & Charges">
          <Row>
            <Label width={LBL}>Handling</Label>
            <TextInput ariaLabel="Handling" value={data.drayHandlingCharge} onChange={ch('drayHandlingCharge')} width={120} readOnly={!onWarehouseChange} />
            <Label>Per</Label>
            <SelectInput ariaLabel="Handling Unit Type" value={data.drayHandlingUnitType} onChange={ch('drayHandlingUnitType')} options={dedupOpts(data.drayHandlingUnitType, HANDLING_UNIT_TYPES)} disabled={!onWarehouseChange} />
            {handlingGuard && (
              <span className={`text-[10.5px] whitespace-nowrap ${handlingBelowMin ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-400 dark:text-slate-500'}`}>
                {handlingBelowMin ? `below min $${handlingGuard.minimumRate}` : `std $${handlingGuard.standardRate} / min $${handlingGuard.minimumRate}`}
              </span>
            )}
          </Row>
          <Row>
            <Label width={LBL}>Handling With Charges</Label>
            {/* V1 fv_handlingwithcharges: highlighted when handling is NOT folded
                into the dray (includehandlingindray != 'Y'). Read-only either way. */}
            <TextInput value={data.handlingWithCharges} highlight={data.includeHandlingInDray !== 'Y' ? 'yellow' : undefined} width={120} />
          </Row>
          <Row>
            <Label width={LBL}>Dray/Transload</Label>
            <SelectInput ariaLabel="In Dray" value={data.includeHandlingInDray} onChange={ch('includeHandlingInDray')} options={['Y', 'N']} width={56} disabled={!onWarehouseChange} />
          </Row>

          <AutoCalcRow label="Total Dray & Handling" value={totalDrayHandling} labelWidth={LBL} />

          <Row>
            <Label width={LBL}>FSC / Type</Label>
            <TextInput value={data.fuelSurcharge} onChange={ch('fuelSurcharge')} placeholder="—" width={96} readOnly={!onWarehouseChange} />
            <SelectInput value={data.fuelType} onChange={ch('fuelType')} options={dedupOpts(data.fuelType, FUEL_TYPES_WAREHOUSE)} width={88} disabled={!onWarehouseChange} />
          </Row>
        </Card>

        <ProductRatesCard
          rates={data.productRates ?? []}
          onAdd={onAddProductRate}
          onUpdate={onUpdateProductRate}
          onDelete={onDeleteProductRate}
          onLookup={onProductCodeLookup}
          onWhseCustomerLookup={onWhseCustomerLookup}
        />
      </div>

      {createMode && <p className="text-xs text-slate-500">Create the quote to add product rates.</p>}
      {/* Handling In / Out items (quote.handlingquote) — V1 shows two lists
          on the edit screen (editrecord_quote.pl:162-177). */}
      <HandlingItemsCard
        items={handlingItems}
        catalog={handlingCatalog}
        onAdd={onAddHandling}
        onChange={onChangeHandling}
        onDelete={onDeleteHandling}
      />
    </>
  );
};

const ProductRatesCard: React.FC<{
  rates: ProductRateRow[];
  onAdd?: (p: ProductRateFieldsPayload) => void;
  onUpdate?: (id: number, p: ProductRateFieldsPayload) => void;
  onDelete?: (id: number) => void;
  onLookup?: (customercode: number) => Promise<Array<{ recno: number; sku: string; description: string; masterUom: string }>>;
  onWhseCustomerLookup?: (q: string) => Promise<WhseCustomerRow[]>;
}> = ({ rates, onAdd, onUpdate, onDelete, onLookup, onWhseCustomerLookup }) => {
  const editable = Boolean(onAdd && onLookup);
  // Inline rate edit — only the rate/type fields change; the SKU identity stays.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [eStorageRate, setEStorageRate] = useState('');
  const [eStorageType, setEStorageType] = useState('');
  const [eHandlingRate, setEHandlingRate] = useState('');
  const [eHandlingType, setEHandlingType] = useState('');

  const beginEdit = (pr: ProductRateRow) => {
    setEditingId(pr.id);
    setEStorageRate(pr.storageRate ?? '');
    setEStorageType(pr.storageType ?? '');
    setEHandlingRate(pr.handlingRate ?? '');
    setEHandlingType(pr.handlingType ?? '');
  };
  const saveEdit = (id: number) => {
    if (!onUpdate) return;
    onUpdate(id, {
      storagerate: Number(eStorageRate) || 0,
      storagetype: eStorageType,
      handlingrate: Number(eHandlingRate) || 0,
      handlingtype: eHandlingType,
    });
    setEditingId(null);
  };
  const [adding, setAdding] = useState(false);
  const [custCode, setCustCode] = useState('');
  const [skus, setSkus] = useState<Array<{ recno: number; sku: string; description: string; masterUom: string }>>([]);
  const [skuRecno, setSkuRecno] = useState(0);
  const [storageRate, setStorageRate] = useState('');
  const [storageType, setStorageType] = useState('');
  const [handlingRate, setHandlingRate] = useState('');
  const [lookupErr, setLookupErr] = useState<string | null>(null);

  // N60: warehouse-customer name typeahead — one call returns the customer +
  // its product codes (no separate code-then-SKU round trip).
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState<WhseCustomerRow[]>([]);
  const [custOpen, setCustOpen] = useState(false);
  useEffect(() => {
    const q = custQuery.trim();
    if (!onWhseCustomerLookup || q.length < 2) { setCustResults([]); setCustOpen(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await onWhseCustomerLookup(q);
        if (!cancelled) { setCustResults(rows); setCustOpen(true); }
      } catch { if (!cancelled) setCustResults([]); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custQuery]);

  const pickWhseCustomer = (row: WhseCustomerRow) => {
    setCustCode(String(row.customercode));
    setSkus(row.productCodes.map((p) => ({
      recno: p.recno, sku: p.sku, description: '', masterUom: p.masterUom,
    })));
    setSkuRecno(0);
    setCustQuery(row.custname);
    setCustOpen(false);
    setLookupErr(row.productCodes.length ? null : 'No product codes for that customer.');
  };

  const runLookup = async () => {
    const code = Number(custCode) || 0;
    if (!onLookup || !code) return;
    setLookupErr(null);
    try {
      const rows = await onLookup(code);
      setSkus(rows);
      if (!rows.length) setLookupErr('No product codes for that customer.');
    } catch {
      setLookupErr('Lookup failed.');
    }
  };

  const submit = () => {
    if (!onAdd || !skuRecno) return;
    onAdd({
      whsecustomercode: Number(custCode) || 0,
      productcoderecno: skuRecno,
      storagerate: Number(storageRate) || 0,
      storagetype: storageType,
      handlingrate: Number(handlingRate) || 0,
    });
    setAdding(false);
    setSkuRecno(0); setStorageRate(''); setHandlingRate(''); setStorageType('');
  };

  const inputCls = 'border border-slate-300 rounded px-1.5 py-1 text-[12px] bg-white text-slate-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

  return (
    <Card
      icon={<TicketIcon className="h-3.5 w-3.5" />}
      title="Product Rates"
      right={
        <div className="flex items-center gap-3">
          {rates.length > 0 && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400">{rates.length}</span>
          )}
          <button
            type="button"
            onClick={editable ? () => setAdding((v) => !v) : undefined}
            className="text-[11.5px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
          >
            + Add Product Rate
          </button>
        </div>
      }
    >
      {adding && editable && (
        <div className="mb-2 p-2 bg-blue-50/70 border border-blue-200 rounded flex flex-col gap-1.5 dark:bg-blue-900/20 dark:border-blue-800">
          {onWhseCustomerLookup && (
            <div className="relative">
              <input
                value={custQuery}
                onChange={(e) => setCustQuery(e.target.value)}
                placeholder="Search warehouse customer by name…"
                aria-label="Warehouse customer search"
                className={`${inputCls} w-full`}
              />
              {custOpen && custResults.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg dark:bg-slate-800 dark:border-slate-700">
                  {custResults.map((c) => (
                    <button
                      key={c.customercode}
                      type="button"
                      onClick={() => pickWhseCustomer(c)}
                      className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-blue-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                    >
                      <span className="font-semibold text-slate-800 dark:text-slate-100">{c.custname}</span>
                      <span className="ml-2 text-[11px] text-slate-500 dark:text-slate-400">
                        {[c.city, c.state].filter(Boolean).join(', ')} · {c.productCodes.length} SKU{c.productCodes.length === 1 ? '' : 's'} · #{c.customercode}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-1.5">
            <input value={custCode} onChange={(e) => setCustCode(e.target.value)} placeholder="Whse customer #" aria-label="Warehouse customer code" className={`${inputCls} w-[120px]`} />
            <button type="button" onClick={() => { void runLookup(); }} className="px-2 py-1 text-[11px] font-semibold text-blue-700 bg-white border border-blue-300 rounded dark:bg-gray-800 dark:text-blue-300 dark:border-blue-800">Find SKUs</button>
            <select
              value={skuRecno ? String(skuRecno) : ''}
              onChange={(e) => setSkuRecno(Number(e.target.value) || 0)}
              aria-label="Product code"
              className={`${inputCls} flex-1`}
            >
              <option value="">— select SKU —</option>
              {skus.map((s) => (
                <option key={s.recno} value={s.recno}>{s.sku}{s.description ? ` — ${s.description}` : ''}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-1.5 items-center">
            <input value={storageRate} onChange={(e) => setStorageRate(e.target.value)} placeholder="Storage rate" aria-label="Storage rate" className={`${inputCls} w-[100px]`} />
            <input value={storageType} onChange={(e) => setStorageType(e.target.value)} placeholder="Per (PLT…)" aria-label="Storage type" className={`${inputCls} w-[90px]`} />
            <input value={handlingRate} onChange={(e) => setHandlingRate(e.target.value)} placeholder="Handling rate" aria-label="Handling rate" className={`${inputCls} w-[100px]`} />
            <button type="button" onClick={submit} disabled={!skuRecno} className="ml-auto px-2 py-1 text-[11px] font-semibold text-white bg-blue-600 rounded disabled:opacity-40">Add</button>
            <button type="button" onClick={() => setAdding(false)} className="px-2 py-1 text-[11px] text-slate-500 dark:text-slate-400">Cancel</button>
          </div>
          {lookupErr && <div className="text-[11px] text-red-600 dark:text-red-400">{lookupErr}</div>}
        </div>
      )}
      {rates.length === 0 && !adding ? (
        <div className="px-3 py-6 text-center text-slate-400 dark:text-slate-500 text-[12px] bg-slate-50 dark:bg-slate-900/40 border border-dashed border-slate-200 dark:border-slate-700 rounded-md">
          No product rates on this revision yet.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rates.map((pr) => {
            if (editingId === pr.id) {
              return (
                <div
                  key={pr.id}
                  className="px-2.5 py-2 bg-blue-50/70 border border-blue-200 rounded text-[12px] flex items-center gap-1.5 flex-wrap dark:bg-blue-900/20 dark:border-blue-800"
                >
                  <span className="text-slate-600 dark:text-slate-300 truncate flex-1 min-w-[120px]">{pr.sku || pr.description || '—'}</span>
                  <input value={eStorageRate} onChange={(e) => setEStorageRate(e.target.value)} placeholder="Storage rate" aria-label="Storage rate" className={`${inputCls} w-[90px]`} />
                  <input value={eStorageType} onChange={(e) => setEStorageType(e.target.value)} placeholder="Per" aria-label="Storage type" className={`${inputCls} w-[80px]`} />
                  <input value={eHandlingRate} onChange={(e) => setEHandlingRate(e.target.value)} placeholder="Handling rate" aria-label="Handling rate" className={`${inputCls} w-[90px]`} />
                  <input value={eHandlingType} onChange={(e) => setEHandlingType(e.target.value)} placeholder="Per" aria-label="Handling type" className={`${inputCls} w-[80px]`} />
                  <button type="button" onClick={() => saveEdit(pr.id)} aria-label="Save product rate" className="px-2 py-1 text-[11px] font-semibold text-white bg-blue-600 rounded">Save</button>
                  <button type="button" onClick={() => setEditingId(null)} className="px-2 py-1 text-[11px] text-slate-500 dark:text-slate-400">Cancel</button>
                </div>
              );
            }
            const usingStorage = parseFloat(pr.storageRate) > 0;
            const rate = usingStorage ? pr.storageRate : pr.handlingRate;
            const unit = usingStorage ? (pr.storageType || pr.masterUom) : (pr.handlingType || pr.masterUom);
            return (
              <div
                key={pr.id}
                className="px-2.5 py-2 bg-slate-50 border border-slate-200 rounded text-[12px] text-slate-900 flex items-center gap-2 flex-wrap dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
              >
                <span className="text-[11.5px] font-semibold text-blue-600 dark:text-blue-400">${rate}</span>
                <span className="text-slate-400 dark:text-slate-500">per</span>
                <span className="font-semibold">{unit || '—'}</span>
                <span className="text-slate-300 dark:text-slate-600">·</span>
                <span className="text-slate-600 dark:text-slate-300 truncate">{pr.sku || pr.description || '—'}</span>
                {onUpdate && (
                  <button
                    type="button"
                    onClick={() => beginEdit(pr)}
                    aria-label="Edit product rate"
                    className="ml-auto text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Edit
                  </button>
                )}
                {editable && onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(pr.id)}
                    aria-label="Remove product rate"
                    className={`${onUpdate ? '' : 'ml-auto '}text-[11px] text-red-600 dark:text-red-400 hover:underline`}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};


const HandlingItemsCard: React.FC<{
  items: HandlingItemRow[];
  catalog: HandlingCatalogRow[];
  onAdd?: (p: { handlingitemrecno: number; handlingtype: 'handlingin' | 'handlingout'; handlingratetype: string; handlingrate: number; handlingunittype?: string }) => void;
  onChange?: (id: number, patch: { handlingratetype?: string; handlingrate?: number; handlingunittype?: string }) => void;
  onDelete?: (id: number) => void;
}> = ({ items, catalog, onAdd, onChange, onDelete }) => {
  const editable = Boolean(onChange);
  const [adding, setAdding] = useState(false);
  const [addRecno, setAddRecno] = useState(0);
  const [addGroup, setAddGroup] = useState<'handlingin' | 'handlingout'>('handlingin');
  const [addType, setAddType] = useState('');
  const [addRate, setAddRate] = useState('');
  const [addUnit, setAddUnit] = useState('');
  const unitOptions = [...new Set([
    ...HANDLING_UNIT_TYPES, ...STORAGE_UNIT_TYPES, 'FOOT', 'SQUARE FEET', 'HOUR', 'MAN HOUR',
    ...catalog.map((c) => c.unitType), ...items.map((i) => i.unitType),
  ].filter(Boolean))];

  const submitAdd = () => {
    if (!onAdd || !addRecno) return;
    const entry = catalog.find((c) => c.recno === addRecno);
    onAdd({
      handlingitemrecno: addRecno,
      handlingtype: addGroup,
      handlingratetype: addType,
      handlingrate: Number(addRate) || Number(entry?.defaultRate) || 0,
      handlingunittype: addUnit,
    });
    setAdding(false); setAddRecno(0); setAddType(''); setAddRate(''); setAddUnit('');
  };

  const group = (g: 'handlingin' | 'handlingout') => items.filter((i) => i.handlingType === g);

  const renderRows = (rows: HandlingItemRow[]) => rows.map((i) => {
    // #13: warn when the entered rate is below the catalog item's minimum.
    const itemMin = Number(catalog.find((c) => c.recno === i.handlingItemRecno)?.minimumRate) || 0;
    const itemBelowMin = itemMin > 0 && (Number(i.rate) || 0) > 0 && (Number(i.rate) || 0) < itemMin;
    return (
    <div key={i.id} className="flex items-center gap-2 px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700 text-[12px]">
      <span className="flex-1 font-semibold text-slate-900 dark:text-slate-100 truncate">{i.description || '—'}</span>
      <SelectInput
        value={i.rateType}
        onChange={editable ? (v) => onChange!(i.id, { handlingratetype: v }) : undefined}
        options={withCurrent(i.rateType, CHARGE_RATE_TYPES)}
        width={120}
      />
      {editable ? (
        <input
          defaultValue={i.rate}
          aria-label="Handling rate"
          onBlur={(e) => {
            if (e.target.value !== i.rate) onChange!(i.id, { handlingrate: Number(e.target.value) || 0 });
          }}
          className="w-[90px] border border-slate-300 rounded px-1.5 py-1 text-[12px] bg-white text-slate-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
      ) : (
        <TextInput value={i.rate} width={90} />
      )}
      {itemBelowMin && <span className="text-[10.5px] text-red-600 dark:text-red-400 font-semibold whitespace-nowrap">min ${itemMin}</span>}
      <SelectInput value={i.unitType} options={withCurrent(i.unitType, unitOptions)}
        onChange={editable ? (v) => onChange!(i.id, { handlingunittype: v }) : undefined}
        ariaLabel={`Per unit for ${i.description}`} width={110} placeholder="Per Unit" />
      {editable && onDelete && (
        <button
          type="button"
          onClick={() => onDelete(i.id)}
          aria-label="Remove handling item"
          className="w-5 h-5 inline-flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-red-600"
        >
          ×
        </button>
      )}
    </div>
    );
  });

  return (
    <Card
      icon={<TicketIcon className="h-3.5 w-3.5" />}
      title="Handling Items"
      dark
      flushBody
      right={
        <button
          type="button"
          onClick={onAdd ? () => setAdding((v) => !v) : undefined}
          className="text-[11.5px] font-semibold text-blue-300 hover:text-blue-200"
        >
          + Add New
        </button>
      }
    >
      {adding && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border-b border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
          <select
            value={addRecno ? String(addRecno) : ''}
            onChange={(e) => {
              const recno = Number(e.target.value) || 0;
              setAddRecno(recno);
              setAddUnit(catalog.find((c) => c.recno === recno)?.unitType || '');
              if (!addRate) {
                const entry = catalog.find((c) => c.recno === recno);
                if (entry?.defaultRate) setAddRate(entry.defaultRate);
              }
            }}
            aria-label="Handling item"
            className="flex-1 border border-slate-300 rounded px-1.5 py-1 text-[12px] bg-white text-slate-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">— select handling item —</option>
            {catalog.map((c) => (
              <option key={c.recno} value={c.recno}>{c.description}</option>
            ))}
          </select>
          <SelectInput value={addGroup} onChange={(v) => setAddGroup(v as 'handlingin' | 'handlingout')} options={['handlingin', 'handlingout']} width={110} />
          <SelectInput value={addType} onChange={setAddType} options={['', ...CHARGE_RATE_TYPES]} width={120} placeholder="Rate type" />
          <TextInput value={addRate} onChange={setAddRate} width={90} placeholder="Rate" />
          <label className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            Per Unit
            <SelectInput value={addUnit} onChange={setAddUnit} options={withCurrent(addUnit, unitOptions)}
              ariaLabel="Handling item per unit" width={110} placeholder="Select..." />
          </label>
          <button type="button" onClick={submitAdd} disabled={!addRecno} className="px-2 py-1 text-[11px] font-semibold text-white bg-blue-600 rounded disabled:opacity-40">Add</button>
          <button type="button" onClick={() => setAdding(false)} className="px-2 py-1 text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">Cancel</button>
        </div>
      )}
      {items.length === 0 && !adding ? (
        <div className="px-4 py-6 text-center text-slate-400 dark:text-slate-500 text-[12.5px] bg-white dark:bg-gray-800">
          No handling items on this revision.
        </div>
      ) : (
        <div className="grid grid-cols-2 bg-white dark:bg-gray-800">
          <div className="border-r border-slate-100 dark:border-slate-700">
            <div className="px-2.5 py-1.5 text-[10.5px] font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700">Handling In</div>
            {renderRows(group('handlingin'))}
          </div>
          <div>
            <div className="px-2.5 py-1.5 text-[10.5px] font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700">Handling Out</div>
            {renderRows(group('handlingout'))}
          </div>
        </div>
      )}
    </Card>
  );
};

const Divider: React.FC = () => <div className="h-px bg-slate-100 dark:bg-slate-700 my-2" />;

/** Plain sum of dray + handling. V1 has more nuanced logic (whether handling
 *  is included in dray, etc.); for the v1 display we just add them. */
function computeTotalDrayHandling(d: WarehouseFields): string {
  // V1 fv_totaldrayhandling (formatview_quote.pl:1415): draywithcharges +
  // handlingwithcharges — the WITH-CHARGES totals, not the base dray/handling
  // rates (the prior version summed the wrong fields).
  const dwc = parseFloat(d.drayWithCharges) || 0;
  const hwc = parseFloat(d.handlingWithCharges) || 0;
  return (dwc + hwc).toFixed(2);
}

export default WarehouseTab;
